<template>
  <MdtModal
    :title="'general_add_pictures' | translate"
    @cancel="$emit('close')"
    @close="$emit('close')"
    @save="saveImages">
    <div class="section">
      <div class="header flex-center">
        {{ 'general_selected' | translate }}
        -
        {{ `${editImages.length}/${maxImagesCount}` }}
      </div>
      <vue-scroll>
        <div class="wrapper">
          <Draggable
            :list="dummyList"
            :handle="'.mdt-image'"
            :options="draggableOptions"
            :move="checkMove"
            class="draggable"
            @start="onDragStart"
            @end="onDragEnd">
            <MdtFileUpload
              :upload-url="uploadUrl"
              :extensions="allowedExtensions"
              :disabled="editImages.length >= maxImagesCount"
              :class="{ disabled: editImages.length >= maxImagesCount }"
              test-key="upload-image-button"
              class="upload-button flex-center"
              @uploaded="onImageUpload" />
            <div
              v-for="(img, i) in editImages"
              :key="`${i}-${img.image}`"
              :data-test="`uploaded-image-${i}`"
              class="image-wrapper flex-center">
              <img
                v-show="img.loaded"
                :src="img.imageUrl"
                class="mdt-image"
                @load="img.loaded = true;"
                @error="img.loaded = false;">
              <i
                v-if="!img.loaded"
                class="fas fa-image no-image" />
              <div
                class="icon-delete-wrapper flex-center pointer"
                @click="deleteImage(img)">
                <i class="fas fa-trash icon-delete" />
              </div>
            </div>
          </Draggable>
        </div>
      </vue-scroll>
    </div>
  </MdtModal>
</template>

<script>
import { pdf, array } from '@/utility';

export default {
  name: 'MdtImageUpload',
  props: {
    images: {
      type: Array,
      required: true,
    },
    tempImages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const { settings } = this.$store.state.bootstrap;
    const { baseBackendUrl } = this.$store.getters;

    return {
      baseBackendUrl,
      dummyList: [], // draggable needs to bind list for events to work
      editImages: [],
      maxImagesCount: settings.maxNumObjectImages,
      allowedExtensions: settings.allowedImageTypes,
      uploadUrl: 'api/v1/upload-images?bucket=true',
      draggableOptions: {
        animation: 230,
        setData: (dataTransfer) => {
          dataTransfer.dropEffect = 'none';
          dataTransfer.effectAllowed = 'none';
        },
        forceFallback: true,
      },
    };
  },
  mounted() {
    this.editImages = JSON.parse(JSON.stringify(this.images));
  },
  methods: {
    onImageUpload(imageList) {
      const list = imageList.map((url) => ({
        id: 0,
        image: url,
        imageUrl: `${this.baseBackendUrl}${url}`,
        loaded: false,
      }));

      // collect temp images
      const tempImages = [...this.tempImages, ...list.map((image) => image.image)];
      // update temp images
      this.$emit('tempImagesData', tempImages);

      // Check images count, remove overcounted images
      if ((this.editImages.length + list.length) > this.maxImagesCount) {
        list.splice(this.maxImagesCount - (this.editImages.length + list.length));
      }

      list.forEach((item) => {
        /*
        * Loop trough list of images and check if image is protected or unprotected
        * If image is protected, fetch image from backend and create blob object for image preview
        */
        const imgObject = this;
        if (item.image.startsWith('/protected/') || item.image.startsWith('protected/')) {
          pdf.content(item.image).then((response) => {
            const blob = new Blob([response.data], { type: this.fileMimeType });
            item.imageUrl = window.URL.createObjectURL(blob);
            imgObject.editImages = [...imgObject.editImages, item];
          }).catch(this.showResponseError);
        } else {
          imgObject.editImages = [...imgObject.editImages, item];
        }
      });
    },
    deleteImage(image) {
      const index = this.editImages.findIndex((imgObj) => imgObj.image === image.image);
      if (index > -1) {
        this.editImages.splice(index, 1);
      }
    },
    saveImages() {
      const images = this.editImages.map((img) => ({
        id: img.id,
        image: img.image,
        imageUrl: img.imageUrl,
        loaded: false,
      }));

      this.$emit('imagesData', images);
      this.$emit('close');
    },
    checkMove(event) {
      return !event.related?.classList.contains('upload-button');
    },
    onDragStart() {
      // mdtImageDrag is used for droppable placeholder in MdtFileUpload component
      window.mdtImageDrag = true;
    },
    onDragEnd(event) {
      // mdtImageDrag is used for droppable placeholder in MdtFileUpload component
      window.mdtImageDrag = false;
      let { oldIndex, newIndex } = event;
      // lowerise indexes by 1 to ignore first upload-button item
      array.reorderPositions(this.editImages, --oldIndex, --newIndex);
    },
  },
};
</script>

<style lang="scss" scoped>
.section {
  width: 880px;
}

.header {
  padding-bottom: 10px;
  font-size: 1.125rem;
  font-weight: $font-weight-bold;
}

.wrapper {
  display: flex;
  flex: 1 1 auto;
  max-height: 70vh;
}

.draggable {
  display: flex;
  flex-wrap: wrap;
}

.upload-button,
.image-wrapper {
  flex: 0 0 200px;
  position: relative;
  width: 200px;
  height: 140px;
  min-height: 140px;
  margin: 10px;
  background-color: #e2e2e2;
  border-radius: 4px;
  overflow: hidden;
}

.upload-button {
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;

    ::v-deep .input-file {
      cursor: not-allowed;
    }
  }

  ::v-deep {
    .options-info {
      font-size: 0.7rem;
      bottom: 8px;
    }
    .dropbox,
    .progress {
      min-height: 140px;
    }
  }
}

.image-wrapper {
  display: flex;
  flex-wrap: wrap;
  cursor: grab;

  &.sortable-chosen {
    border: 4px dashed $color-white;
    border-radius: 4px;
    box-shadow: 0 3px 14px #00000040;

    img {
      cursor: grabbing !important;
    }
  }

  &.sortable-drag {
    display: none;
  }

  &:hover {
    img,
    .no-image {
      transform: scale(1.1);
    }

    .icon-position-wrapper {
      display: block;
    }
  }

  img,
  .no-image,
  .icon-delete-wrapper,
  .icon-position-wrapper > i {
    transition: transform 0.4s ease,
                background-color 0.4s ease;
  }

  img {
    height: 100%;
  }

  .no-image {
    color: $color-text-secondary;
    font-size: 2.5rem;
  }

  .icon-delete-wrapper {
    position: absolute;
    bottom: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    background-color: rgba($color-back-primary-rgb, 0.5);
    border-radius: 2px;

    &:hover {
      background-color: rgba($color-back-primary-rgb, 0.8);
    }
  }
}
</style>
